// LLM generated - Model GPT-5.2
import { createStyles } from '@mantine/core';

/** Matches board Popular Picks carousel card width — use for search grid columns. */
export const POPULAR_PICK_CARD_WIDTH_PX = 180;

/** Viewports narrower than iPhone Plus width — tighten card padding and pick buttons to avoid clipping. */
const NARROW_CARD_MAX_WIDTH_PX = 413;

export const usePopularPickCardStyles = createStyles((theme) => ({
  card: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.other.spacing._4,
    padding: theme.other.spacing._8,
    [`@media (max-width: ${NARROW_CARD_MAX_WIDTH_PX}px)`]: {
      paddingTop: theme.other.spacing._8,
      paddingBottom: theme.other.spacing._8,
      paddingLeft: theme.other.spacing._4,
      paddingRight: theme.other.spacing._4,
    },
    backgroundColor: theme.white,
    borderRadius: theme.other.spacing._16,
    overflow: 'hidden',
    cursor: 'pointer',
    border: '2px solid transparent',
    appearance: 'none',
    boxSizing: 'border-box',
    width: '100%',
    minWidth: 0,
    height: 226,
    textAlign: 'left',
  },
  cardSelected: {
    borderColor: theme.colors.blue[9],
  },
  cardSelectedAlternate: {
    borderColor: theme.colors.yellow[4],
  },
  cardDisabled: {
    opacity: 0.45,
  },

  cardTeamLogo: {
    position: 'absolute',
    bottom: 0,
    left: '-100%',
    opacity: 0.2,
    pointerEvents: 'none',
    aspectRatio: '1 / 1',
  },

  cardGradient: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #FFFFFF 40%)',
    pointerEvents: 'none',
    borderRadius: 'inherit',
  },

  cardInnerShadow: {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    borderRadius: 'inherit',
    boxShadow: `inset 0px 0px ${theme.other.spacing._8}px 0px white`,
  },

  cardUpper: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingBottom: theme.other.spacing._12,
    width: '100%',
    isolation: 'isolate',
  },

  cardLeagueRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    width: '100%',
    height: 20,
    marginBottom: -theme.other.spacing._12,
    position: 'relative',
    zIndex: 2,
  },

  cardLeague: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: theme.other.spacing._24,
    paddingLeft: /* eslint-disable-line local-rules/use-theme-tokens */ 3,
    paddingRight: /* eslint-disable-line local-rules/use-theme-tokens */ 3,
  },

  cardLeagueIcon: {
    marginBottom: theme.other.spacing._2,
    display: 'flex',
    alignItems: 'center',
  },

  cardLeaguePosition: {
    color: theme.colors.gray2[3],
    textTransform: 'uppercase',
  },

  cardAvatarWrap: {
    position: 'relative',
    flexShrink: 0,
    zIndex: 1,
    width: theme.other.spacing._64,
    height: theme.other.spacing._64,
    marginBottom: -theme.other.spacing._12,
  },

  cardPlayerImage: {
    position: 'relative',
    width: '100%',
    height: '100%',
    borderRadius: theme.other.spacing._64,
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    border: `2px solid ${theme.white}`,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: `0px ${theme.other.spacing._8}px ${theme.other.spacing._40}px 0px rgba(0, 0, 0, 0.1)`,
  },

  injuryTagAvatar: {
    position: 'absolute',
    zIndex: 3,
    '--player-card-tag-right': 'auto',
    '--player-card-tag-left': '50%',
    '--player-card-tag-transform': 'translateX(-50%)',
  },

  cardPlayerInfo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
    position: 'relative',
    zIndex: 2,
  },

  cardPlayerName: {
    textAlign: 'center',
    width: '100%',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },

  cardMatchup: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },

  cardMatchupText: {
    textAlign: 'center',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  cardStat: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: 56,
    paddingBottom: theme.other.spacing._8,
    position: 'relative',
    zIndex: 2,
  },

  cardStatValue: {
    marginBottom: -theme.other.spacing._4,
  },

  cardStatLabel: {
    textAlign: 'center',
    width: '100%',
  },

  cardButtons: {
    display: 'flex',
    gap: theme.other.spacing._8,
    width: '100%',
    position: 'relative',
    zIndex: 2,
    [`@media (max-width: ${NARROW_CARD_MAX_WIDTH_PX}px)`]: {
      gap: theme.other.spacing._4,
    },
  },

  cardButton: {
    '&&': {
      flex: 1,
      minWidth: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.other.spacing._2,
      height: 36,
      borderRadius: theme.other.spacing._8,
      paddingLeft: theme.other.spacing._4,
      paddingRight: theme.other.spacing._4,
    },
    [`@media (max-width: ${NARROW_CARD_MAX_WIDTH_PX}px)`]: {
      '&&': {
        height: 32,
        minHeight: 32,
      },
      gap: theme.other.spacing._2,
      paddingLeft: theme.other.spacing._4,
      paddingRight: theme.other.spacing._4,
      '& svg': {
        width: 14,
        height: 14,
      },
    },
  },
  cardButtonWithOdds: {
    '&&': {
      justifyContent: 'flex-start',
      height: 'auto',
      minHeight: 36,
      paddingLeft: theme.other.spacing._8,
      paddingRight: theme.other.spacing._8,
    },
    [`@media (max-width: ${NARROW_CARD_MAX_WIDTH_PX}px)`]: {
      '&&': { minHeight: 32 },
    },
  },
  cardButtonIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 16,
    height: 16,
    flexShrink: 0,
    marginLeft: -theme.other.spacing._2,
  },
}));
